import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  FolderOpen,
  BarChart3,
  Database,
  BookOpen,
  Users,
  Lightbulb,
  FlaskConical,
  LucideIcon,
} from 'lucide-react'
import { cn } from '@/utils/cn'
import { useAuth } from '@/contexts/AuthContext'
import { useTranslation } from 'react-i18next'

interface TabItem {
  name: string
  href: string
  icon: LucideIcon
  adminOnly?: boolean
}

const MobileBottomTabBar: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const { isAdmin } = useAuth()

  // Define tabs - ordered: guidelines, manual, sessions, reports, catalog (admin), testing (admin), admin (admin)
  const allTabs: TabItem[] = [
    { name: 'Guidelines', href: '/guidelines', icon: Lightbulb },
    { name: t('nav.manual'), href: '/manual', icon: BookOpen },
    { name: t('nav.sessions'), href: '/sessions', icon: FolderOpen },
    { name: t('nav.reports'), href: '/reports', icon: BarChart3 },
    { name: t('nav.catalog'), href: '/catalog', icon: Database, adminOnly: true },
    { name: 'Testing', href: '/testing', icon: FlaskConical, adminOnly: true },
    { name: t('nav.admin'), href: '/admin', icon: Users, adminOnly: true },
  ]

  // Filter tabs based on role
  const tabs = allTabs.filter((tab) => !tab.adminOnly || isAdmin)

  const isActive = (href: string) => {
    return (
      location.pathname === href ||
      (href === '/sessions' && location.pathname.startsWith('/sessions/')) ||
      (href === '/reports' && location.pathname.startsWith('/reports/')) ||
      (href === '/testing' && location.pathname.startsWith('/testing/'))
    )
  }

  return (
    <nav
      className={cn(
        'fixed bottom-0 left-0 right-0 z-50',
        'bg-white border-t border-gray-200 shadow-lg',
        'md:hidden', // Only visible on mobile
        'pb-[env(safe-area-inset-bottom)]' // iOS safe area
      )}
    >
      <div className="flex justify-around items-center h-16">
        {tabs.map((tab) => {
          const Icon = tab.icon
          const active = isActive(tab.href)

          return (
            <Link
              key={tab.href}
              to={tab.href}
              className={cn(
                'flex flex-col items-center justify-center',
                'flex-1 h-full relative',
                'transition-colors duration-200',
                'min-w-0', // Allow flex items to shrink
                active
                  ? 'text-primary-600'
                  : 'text-gray-500 active:text-gray-700'
              )}
            >
              <Icon
                className={cn(
                  'h-5 w-5 mb-1',
                  active && 'text-primary-600'
                )}
              />
              <span
                className={cn(
                  'text-xs font-medium truncate px-1',
                  active ? 'text-primary-600' : 'text-gray-500'
                )}
              >
                {tab.name}
              </span>
              {/* Active indicator */}
              {active && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary-500 rounded-full" />
              )}
            </Link>
          )
        })}
      </div>
    </nav>
  )
}

export default MobileBottomTabBar
